import { injector } from 'Injector';
import { toJS } from 'mobx';
import { STORAGE_INTERACTOR_KEY, StorageInteractor } from 'utils/storageInteractor';
import { API } from 'utils/api';
import { Search, Filter, SearchRepo, FilterMeta, SearchArgs } from './Search';
import isNil from 'lodash/isNil';

// Builds the query for the search API's `GET /v2/search/elements`, which reads plural filter keys and `perPage`.
export const buildFilterDTO = (filter: Filter): object => {
  const filterDTO: { [key: string]: string | number | boolean | Array<string> | Array<number> } = {};
  if (filter.page) {
    filterDTO.page = filter.page;
  }
  if (filter.per_page) {
    filterDTO.perPage = filter.per_page;
  }
  if (filter.localeId) {
    // logic per langs
    filterDTO.locales = filter.localeId;
  }
  if (filter.type) {
    filterDTO.type = filter.type;
  }
  if (filter.searchQuery) {
    filterDTO.query = filter.searchQuery;
  }
  if (filter.grades) {
    filterDTO.grades = (toJS(filter.grades).length > 0) ? String(filter.grades) : toJS(filter.grades);
  }
  if (filter.subjects) {
    filterDTO.subjects = (toJS(filter.subjects).length > 0) ? String(filter.subjects) : toJS(filter.subjects);
  }
  if (filter.coreElements) {
    filterDTO.coreElements = (toJS(filter.coreElements).length > 0) ? String(filter.coreElements) : toJS(filter.coreElements);
  }
  if (filter.topics) {
    filterDTO.topics = (toJS(filter.topics).length > 0) ? String(filter.topics) : toJS(filter.topics);
  }
  if (filter.sources) {
    filterDTO.sources = (toJS(filter.sources).length > 0) ? String(filter.sources) : toJS(filter.sources);
  }
  if (filter.goals) {
    filterDTO.goals = (toJS(filter.goals).length > 0) ? String(filter.goals) : toJS(filter.goals);
  }
  if (filter.readingInSubjects) {
    filterDTO.readingInSubjects = (toJS(filter.readingInSubjects).length > 0) ? String(filter.readingInSubjects) : toJS(filter.readingInSubjects);
  }
  return filterDTO;
};
/**
 * The wire shape, which is not the domain shape.
 *
 * `SearchRepo` declares this endpoint returns `Array<Search>` and a `FilterMeta`. Both are
 * classes — `FilterMeta` calls `makeObservable` in its constructor — and the endpoint sends
 * plain JSON, so the values handed back have never actually been either. While the response
 * was `any` that went unnoticed; typing it makes the gap explicit rather than creating it.
 *
 * The two assertions below are the whole of that gap, named and in one place. Closing it
 * means running the payload through a factory the way every other repo does, which is a
 * behaviour change and belongs in its own commit.
 */
interface SearchResponseDTO {
  data: Array<SearchArgs>;
  // Every locale with content of the requested type, whatever the other filters. `filters.locales` only
  // lists the locales of the filtered set.
  locales: Array<{ id: string; name: string }>;
  filters: FilterMeta;
  meta: { pagination: { lastPage: number } };
}

export class SearchApi implements SearchRepo {
  private storageInteractor = injector.get<StorageInteractor>(STORAGE_INTERACTOR_KEY);
  public async getDataSearch(filter: Filter): Promise<{items: Array<Search>, filters: FilterMeta, totalpage: number}>  {
    if (!isNil(filter.searchQuery)) filter.searchQuery = String(filter.searchQuery);
    // No X-Tenant-Id: v2 narrows articles to that tenant, which v1 /api/content stopped doing, so sending it
    // would hide the other tenants' articles the search lists today.
    const response = await API.get<SearchResponseDTO>(`${process.env.REACT_APP_SEARCH}/v2/search/elements`, {
      params: buildFilterDTO(filter),
    });
    return {
      // `as` rather than a construction: the fields line up, but the objects are not
      // `Search` instances and never have been. See SearchResponseDTO above.
      items: response.data.data as unknown as Array<Search>,
      // The language filter offers every locale, not just the selected ones.
      filters: { ...response.data.filters, locales: response.data.locales.map(locale => locale.id) },
      totalpage: response.data.meta.pagination.lastPage
    };
  }
}
